// Known radiator data at 75/65/20: Watts per 1000 mm (i.e., W/m)
// These are manufacturer-provided values used as the baseline for temperature scaling
const RADIATOR_W_PER_M_75_65_20 = {
  10: { 300: 348, 400: 449, 450: 498, 500: 546, 600: 639, 750: 706, 900: 903 },
  11: { 300: 546, 400: 711, 450: 790, 500: 868, 600: 1018, 750: 1230, 900: 1427 },
  20: { 300: 630, 400: 787, 450: 863, 500: 938, 600: 1085, 750: 1154, 900: 1516 },
  21: { 300: 761, 400: 963, 450: 1060, 500: 1156, 600: 1340, 750: 1595, 900: 1861 },
  22: { 300: 961, 400: 1221, 450: 1347, 500: 1470, 600: 1709, 750: 2150, 900: 2388 },
  30: { 300: 874, 400: 1098, 450: 1205, 500: 1309, 600: 1510, 750: 1751, 900: 2069 },
  33: { 300: 1347, 400: 1699, 450: 1869, 500: 2035, 600: 2356, 750: 3119, 900: 3260 },
}

// N-exponents per radiator type (EN 442 standard)
// These values define how radiator output scales with temperature difference
const N_EXPONENTS = {
  10: 1.25,
  11: 1.3,
  20: 1.28,
  21: 1.3,
  22: 1.32,
  30: 1.3,
  33: 1.33,
}

// Temperature condition presets: { flow, return, room }
export const TEMPERATURE_CONDITIONS = {
  '75/65/20': { flow: 75, return: 65, room: 20, label: '75/65/20' },
  '70/55/20': { flow: 70, return: 55, room: 20, label: '70/55/20' },
  '55/45/20': { flow: 55, return: 45, room: 20, label: '55/45/20' },
  '45/35/20': { flow: 45, return: 35, room: 20, label: '45/35/20' },
}

// Default temperature condition
const DEFAULT_TEMPS = TEMPERATURE_CONDITIONS['55/45/20']

// Baseline temperature condition (the stored W/m values are at this condition)
const BASELINE_TEMPS = TEMPERATURE_CONDITIONS['75/65/20']

const types = Object.keys(RADIATOR_W_PER_M_75_65_20)
const heights = Object.keys(RADIATOR_W_PER_M_75_65_20[types[0]]).map((h) => Number(h))
const lengths = [
  300, 400, 450, 500, 600, 750, 900, 1000, 1100, 1200, 1300, 1400, 1600, 1800, 2000, 2300, 2600,
  3000,
]

export const existingRadiatorData = {
  types: types,
  heights: heights,
  lengths: lengths,
}

/**
 * Returns the available temperature condition presets for UI selection.
 * @returns {Array<{key: string, label: string, flow: number, return: number, room: number}>}
 */
export function getTemperatureConditions() {
  return Object.entries(TEMPERATURE_CONDITIONS).map(([key, value]) => ({
    key,
    ...value,
  }))
}

/**
 * Calculates the Logarithmic Mean Temperature Difference (LMTD).
 * Used when the ratio of temperature drops is significant.
 *
 * @param {number} flowTemp - Flow temperature in °C
 * @param {number} returnTemp - Return temperature in °C
 * @param {number} roomTemp - Room temperature in °C
 * @returns {number} LMTD value
 */
function calculateLMTD(flowTemp, returnTemp, roomTemp) {
  const deltaIn = flowTemp - roomTemp
  const deltaOut = returnTemp - roomTemp

  if (deltaIn <= 0 || deltaOut <= 0) {
    throw new Error('Flow and return temperatures must be higher than room temperature.')
  }

  if (Math.abs(deltaIn - deltaOut) < 0.001) {
    // When deltas are nearly equal, LMTD approaches arithmetic mean
    return (deltaIn + deltaOut) / 2
  }

  return (deltaIn - deltaOut) / Math.log(deltaIn / deltaOut)
}

/**
 * Calculates the mean temperature difference (arithmetic mean method).
 * Used when the temperature ratio is high (close to 1).
 *
 * @param {number} flowTemp - Flow temperature in °C
 * @param {number} returnTemp - Return temperature in °C
 * @param {number} roomTemp - Room temperature in °C
 * @returns {number} Mean temperature difference
 */
function calculateMeanDelta(flowTemp, returnTemp, roomTemp) {
  return (flowTemp + returnTemp) / 2 - roomTemp
}

/**
 * Calculates the effective ΔT using either LMTD or mean method based on ratio.
 *
 * @param {number} flowTemp - Flow temperature in °C
 * @param {number} returnTemp - Return temperature in °C
 * @param {number} roomTemp - Room temperature in °C
 * @returns {number} Effective ΔT
 */
function calculateDeltaT(flowTemp, returnTemp, roomTemp) {
  const ratio = (returnTemp - roomTemp) / (flowTemp - roomTemp)

  // Use mean method when ratio is high (temperatures are close), LMTD otherwise
  if (ratio > 0.7) {
    return calculateMeanDelta(flowTemp, returnTemp, roomTemp)
  }
  return calculateLMTD(flowTemp, returnTemp, roomTemp)
}

/**
 * Calculates radiator output (W) at specified temperature conditions using EN 442 scaling.
 *
 * Formula:
 *   watts = W_baseline_per_m * (ΔT_actual / ΔT_baseline)^nExp * (length_mm / 1000)
 *
 * Where:
 *   - W_baseline_per_m is the known output at 55/45/20 (manufacturer data)
 *   - ΔT_actual is calculated using LMTD or mean method
 *   - nExp is the radiator type's specific exponent
 *
 * @param {number|string} type - Radiator type (e.g., 11, 22, 33)
 * @param {number|string} heightMm - Height in mm (e.g., 400)
 * @param {number} lengthMm - Length in mm (e.g., 900)
 * @param {{flow: number, return: number, room: number}} [temps] - Temperature conditions
 * @returns {number} Output in watts (integer)
 */
export function radiatorWattsAtTemp(type, heightMm, lengthMm, temps = DEFAULT_TEMPS) {
  const t = String(type)
  const h = String(heightMm)

  if (!Number.isFinite(lengthMm) || lengthMm <= 0) {
    throw new Error(`Invalid length: ${lengthMm}. Must be a positive number in mm.`)
  }

  const typeRow = RADIATOR_W_PER_M_75_65_20[t]
  if (!typeRow) {
    throw new Error(
      `Unknown radiator type: ${type}. Allowed: ${Object.keys(RADIATOR_W_PER_M_75_65_20).join(', ')}`
    )
  }

  const wBaselinePerM = typeRow[h]
  if (wBaselinePerM == null) {
    throw new Error(
      `Unknown height ${heightMm} mm for type ${type}. Allowed heights: ${Object.keys(typeRow).join(', ')}`
    )
  }

  const nExp = N_EXPONENTS[t]
  if (nExp == null) {
    throw new Error(`No n-exponent defined for type ${type}.`)
  }

  const { flow, return: returnTemp, room } = temps
  const deltaT = calculateDeltaT(flow, returnTemp, room)

  // Calculate baseline deltaT from the baseline temperature condition (55/45/20)
  const baselineDeltaT = calculateDeltaT(
    BASELINE_TEMPS.flow,
    BASELINE_TEMPS.return,
    BASELINE_TEMPS.room
  )

  // Scale from baseline to actual conditions using power law
  const scalingFactor = Math.pow(deltaT / baselineDeltaT, nExp)
  const watts = wBaselinePerM * scalingFactor * (lengthMm / 1000)

  return Math.round(watts)
}

/**
 * Calculates radiator output (W) at 55/45/20 based on fixed type/height lookup.
 * Backward-compatible wrapper around radiatorWattsAtTemp.
 *
 * @param {number|string} type     Radiator type (e.g., 11, 22, 33)
 * @param {number|string} heightMm Height in mm (e.g., 400)
 * @param {number} lengthMm        Length in mm (e.g., 900)
 * @returns {number}               Output in watts (integer)
 */
export function radiatorWatts(type, heightMm, lengthMm) {
  return radiatorWattsAtTemp(type, heightMm, lengthMm, DEFAULT_TEMPS)
}

// // Example:
// // Type 11, height 400 mm, length 900 mm => 635 W
// console.log(radiatorWatts(11, 400, 900)); // 635
